import { Button, Card, Space, Select, Modal, Input, Form, Grid, Table } from "@arco-design/web-react"
import { InvoiceInfo } from "./invoice-info";
import { InvoiceAdd } from "./invoice-add";
import { InvoiceBlank } from "./invoice-blank";
import { InvoiceEdit } from "./invoice-edit";
import { Currency } from "@/components/Numeric";
import { useCallback, useEffect, useState } from "react";
import { CustomerAPI, InvoiceAPI } from "@/apis/sales";
import { Link } from "react-router-dom";
import SaleOrderLink from "@/components/SaleOrderLink";
import CustomerSelect from "@/components/CustomerSelect";
import Reference from "@/components/Reference";
import _ from "lodash";
import { FileUtil } from "@/utils";
import { InvoiceStatusDef } from "@/enums/sales";
import { EnumLabel } from "@/components/Enum";

const { Row } = Grid;

const INVOICE_STATUS = {
    Current: 1,
    'Current Partial': 2,
    'Past Due': 3,
    'Past Due Partial': 4,
    Paid: 5,
    Voided: 6
};

const INVOICE_STATUS_OPTIONS = [
    { label: 'ALL', value: 'ALL' },
    { label: 'Open', value: 'Open' },
    ...Object.entries(INVOICE_STATUS).map(([label, value]) => ({
        label,
        value: label
    }))
];

export const Invoice = () => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [status, setStatus] = useState("Open");
    const [customer, setCustomer] = useState();
    const [totalBalance, setTotalBalance] = useState(0);
    const [totalPastDue, setTotalPastDue] = useState(0);

    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [toOptions, setToOptions] = useState([]);
    const [oepnInvoiceCustomers, setOpenInvoiceCustomers] = useState([]);
    const [form] = Form.useForm();

    const getStatusList = useCallback((status) => {
        if (status === 'ALL') {
            return Object.values(INVOICE_STATUS);
        }
        if (status === 'Open') {
            return [
                INVOICE_STATUS['Current'],
                INVOICE_STATUS['Current Partial'],
                INVOICE_STATUS['Past Due'],
                INVOICE_STATUS['Past Due Partial']
            ];
        }
        return INVOICE_STATUS[status];
    }, []);

    const getList = useCallback(async () => {
        const statusList = getStatusList(status);
        const values = await InvoiceAPI.query(page, { page: page, status: statusList, customerId: customer });
        setData(values.records);
        setTotal(values.total);
    }, [page, status, customer, getStatusList]);

    const getTotalBalanceAndPastDue = useCallback(async () => {
        const statusList = getStatusList(status);
        const totalBalance = await InvoiceAPI.getTotalBalance({ customerId: customer, status: statusList });
        const totalPastDue = await InvoiceAPI.getTotalPastDue({ customerId: customer, status: statusList });
        setTotalBalance(totalBalance);
        setTotalPastDue(totalPastDue);
    }, [status, customer, getStatusList]);

    useEffect(() => {
        getList();
        getTotalBalanceAndPastDue();
    }, [getList, getTotalBalanceAndPastDue]);

    const handleCustomerChange = async (val) => {
        const allContacts = await CustomerAPI.contacts(val);
        setToOptions(_.map(allContacts, (contact) => ({
            label: `${contact.lastName} ${contact.firstName}<${contact.email}>`,
            value: contact.email
        })));
        const contacts = await CustomerAPI.contacts(val, ["ACCOUNTING"]);
        form.setFieldsValue({
            addresses: contacts.map(contact => contact.email)
        });
    }

    return (
        <Card title="Invoices" extra={
            <Space>
                <CustomerSelect
                    placeholder="Customer"
                    style={{ width: 150 }}
                    allowClear
                    onChange={val => setCustomer(val)}
                />
                <Select
                    value={status}
                    options={INVOICE_STATUS_OPTIONS}
                    placeholder="Status"
                    style={{ width: 150 }}
                    showSearch
                    onChange={val => setStatus(val)}
                />
                <Link to={`/workspace/sales/invoice/blank`}>
                    <Button type="primary">Add New Invoice</Button>
                </Link>
                <Button type="primary" onClick={async () => {
                    const res = await InvoiceAPI.download({ status: getStatusList(status), customerId: customer });
                    FileUtil.download(res.data, 'invoices.xlsx');
                }}>Download</Button>
                <Button type="primary" onClick={() => setShowDownloadModal(true)}>Account Statement</Button>
                {showDownloadModal && (
                    <Modal
                        title="Send Account Statement"
                        visible={showDownloadModal}
                        onOk={async () => {
                            const values = await form.validate();
                            await InvoiceAPI.sendInvoiceList(values);
                            setShowDownloadModal(false);
                        }}
                        onCancel={() => setShowDownloadModal(false)}
                        afterOpen={async () => {
                            form.resetFields();
                            const result = await CustomerAPI.openInvoiceCustomers();
                            const formattedResult = _.map(result, customer => ({
                                label: customer.customerName,
                                value: customer.id
                            }));
                            setOpenInvoiceCustomers(formattedResult);
                            const content = await InvoiceAPI.getAccountStatementTemplate();
                            form.setFieldsValue({
                                content: content
                            });
                        }}
                    >
                        <Form form={form} colon>
                            <Form.Item label="Customer" field="customerId" required rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select Customer"
                                    options={oepnInvoiceCustomers}
                                    showSearch
                                    filterOption={(inputValue, option) =>
                                        _.toLower(option.props.value).indexOf(inputValue.toLowerCase()) >= 0 ||
                                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                                    }
                                    onChange={val => handleCustomerChange(val)}
                                />
                            </Form.Item>
                            <Form.Item label="To" field="addresses" required rules={[{ required: true }]}>
                                <Select
                                    mode="multiple"
                                    placeholder="To"
                                    options={toOptions}
                                    filterOption={(inputValue, option) =>
                                        option.props.value.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0 ||
                                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                                    }
                                />
                            </Form.Item>
                            <Form.Item label="Content" field="content" required rules={[{ required: true }]}>
                                <Input.TextArea
                                    placeholder="Content"
                                    style={{ minHeight: 200, width: '100%' }}
                                />
                            </Form.Item>
                        </Form>
                    </Modal>
                )}
            </Space>
        }>
            <Row>
                <Space>
                    Total Balance: <Currency value={totalBalance} />
                    Total Past Due: <Currency value={totalPastDue} />
                </Space>
            </Row>
            <Table rowKey="id" style={{ marginTop: 5 }} columns={[
                {
                    title: "Invoice#", render: (col, record, index) => (
                        <Link to={"/workspace/sales/invoice/" + record.id}>{record.invoiceNo}</Link>
                    )
                },
                { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={InvoiceStatusDef} value={record.status} /> },
                { title: "Invoice Date", dataIndex: "invoiceDate" },
                { title: "Due Date", dataIndex: "dueDate" },
                { title: "Customer", dataIndex: "customerName" },
                { title: "Total Amount", render: (col, record) => <Currency value={record.totalAmount} /> },
                {
                    title: "Reference#",
                    dataIndex: "reference",
                    render: (col, record) => <Reference value={record.reference} />
                },
                { title: "SO#", render: (col, record) => <SaleOrderLink soId={record.soId}>{record.soNo}</SaleOrderLink> },
                { title: "Date Paid", dataIndex: "paidDate" },
            ]} data={data} pagination={{ total, current: page, pageSize: 20 }} onChange={(pagination) => setPage(pagination.current)} />
        </Card>
    )
}

Invoice.Blank = InvoiceBlank;
Invoice.Add = InvoiceAdd;
Invoice.Info = InvoiceInfo;
Invoice.Edit = InvoiceEdit;
